import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, ExternalLink, Gift, Link2, Loader2, PauseCircle, PlayCircle, RefreshCw, Save, ShieldCheck } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { Button } from './ui/Button';

type TermsType = 'NONE' | 'FIXED_AMOUNT' | 'PERCENTAGE';
type ProgrammeState = {
  status: 'NOT_ENABLED' | 'ACTIVE' | 'PAUSED';
  permanent_link: null | {
    token: string; is_active: boolean; expires_at: string | null; revoked_at: string | null; created_at: string;
  };
  current_terms: null | {
    version_number: number; effective_from: string; client_benefit_type: TermsType;
    client_benefit_value: number | null; partner_reward_type: TermsType;
    partner_reward_value: number | null; currency: string;
  };
  terms_version_count: number;
  statistics: {
    total_referrals: number; pending_rewards: number; pending_client_benefits: number; last_referral_at: string | null;
  };
};

type TermsDraft = {
  client_benefit_type: TermsType; client_benefit_value: string;
  partner_reward_type: TermsType; partner_reward_value: string;
};

const emptyTerms: TermsDraft = {
  client_benefit_type: 'NONE', client_benefit_value: '', partner_reward_type: 'NONE', partner_reward_value: ''
};

const websiteBase = () => {
  const configured = process.env.REACT_APP_QUIKPIX_WEBSITE_URL?.replace(/\/$/, '');
  if (configured) return configured;
  return ['localhost', '127.0.0.1'].includes(window.location.hostname) ? 'http://localhost:3002' : '';
};

const statusStyles = {
  NOT_ENABLED: 'border-slate-700 bg-slate-800/70 text-slate-300',
  ACTIVE: 'border-emerald-400/30 bg-emerald-400/10 text-emerald-300',
  PAUSED: 'border-amber-400/30 bg-amber-400/10 text-amber-300'
};

const statusLabel = { NOT_ENABLED: 'Not Enabled', ACTIVE: 'Active', PAUSED: 'Paused' };

export const StaffReferralProgramme: React.FC<{ staffId: string; staffName: string }> = ({ staffId, staffName }) => {
  const [programme, setProgramme] = useState<ProgrammeState | null>(null);
  const [draft, setDraft] = useState<TermsDraft>(emptyTerms);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    const { data, error: loadError } = await supabase.rpc('admin_get_staff_referral_programme', { p_staff_member_id: staffId });
    if (loadError) setError(loadError.message);
    else setProgramme(data as ProgrammeState);
    setLoading(false);
  }, [staffId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!programme?.current_terms) { setDraft(emptyTerms); return; }
    setDraft({
      client_benefit_type: programme.current_terms.client_benefit_type,
      client_benefit_value: programme.current_terms.client_benefit_value?.toString() || '',
      partner_reward_type: programme.current_terms.partner_reward_type,
      partner_reward_value: programme.current_terms.partner_reward_value?.toString() || ''
    });
  }, [programme?.current_terms]);

  const referralUrl = useMemo(() => {
    const base = websiteBase();
    return base && programme?.permanent_link?.token ? `${base}/r/${programme.permanent_link.token}` : '';
  }, [programme?.permanent_link?.token]);

  const flash = (message: string) => {
    setNotice(message);
    window.setTimeout(() => setNotice(''), 2400);
  };

  const run = async (name: string, args: Record<string, unknown>, success: string) => {
    setSaving(true); setError('');
    const { data, error: actionError } = await supabase.rpc(name, args);
    if (actionError) setError(actionError.message);
    else { setProgramme(data as ProgrammeState); flash(success); }
    setSaving(false);
  };

  const saveTerms = async () => {
    const needsClientValue = draft.client_benefit_type !== 'NONE';
    const needsRewardValue = draft.partner_reward_type !== 'NONE';
    if ((needsClientValue && draft.client_benefit_value === '') || (needsRewardValue && draft.partner_reward_value === '')) {
      setError('Enter a value for each selected benefit or reward type.'); return;
    }
    await run('admin_replace_crew_referral_terms', {
      p_staff_member_id: staffId,
      p_terms: {
        client_benefit_type: draft.client_benefit_type,
        client_benefit_value: needsClientValue ? draft.client_benefit_value : null,
        partner_reward_type: draft.partner_reward_type,
        partner_reward_value: needsRewardValue ? draft.partner_reward_value : null
      }
    }, 'New referral terms version created');
  };

  const copyLink = async () => {
    if (!referralUrl) return;
    await navigator.clipboard.writeText(referralUrl);
    flash('Referral link copied');
  };

  if (loading) return (
    <section className="rounded-3xl border border-white/10 bg-slate-950/50 p-6" aria-label="Referral Programme">
      <div className="flex items-center gap-3 text-slate-400"><Loader2 className="animate-spin" size={18}/> Loading referral programme…</div>
    </section>
  );

  return (
    <section className="rounded-3xl border border-brand-neonBlue/20 bg-slate-950/55 p-5 md:p-6 space-y-6" aria-labelledby="staff-referral-heading">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-white/5 pb-4">
        <div>
          <h4 id="staff-referral-heading" className="type-heading flex items-center gap-2 text-base text-brand-neonBlue">
            <Gift size={15}/> Referral Programme
          </h4>
          <p className="mt-2 text-sm text-slate-400">Manage {staffName || 'this crew member'}’s participation and current terms.</p>
        </div>
        {programme && <span className={`rounded-full border px-3 py-1 text-xs font-bold ${statusStyles[programme.status]}`}>{statusLabel[programme.status]}</span>}
      </div>

      {error && <div role="alert" className="rounded-xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-200">{error}</div>}
      {notice && <div role="status" className="rounded-xl border border-emerald-400/30 bg-emerald-400/10 px-4 py-3 text-sm text-emerald-200">{notice}</div>}

      {programme?.status === 'NOT_ENABLED' && (
        <div className="rounded-2xl border border-dashed border-white/15 bg-white/[0.025] p-5">
          <p className="text-sm leading-6 text-slate-300">Enabling creates one Crew referral identity and one permanent, non-expiring referral link. No other staff members are affected.</p>
          <Button className="mt-4" size="sm" disabled={saving} onClick={() => run('admin_enable_crew_referrer', { p_staff_member_id: staffId }, 'Crew referral programme enabled')}>
            {saving ? <Loader2 size={16} className="animate-spin"/> : <ShieldCheck size={16}/>} Enable Crew Referrals
          </Button>
        </div>
      )}

      {programme && programme.status !== 'NOT_ENABLED' && (
        <>
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_180px]">
            <div className="min-w-0 space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                {programme.status === 'ACTIVE' ? (
                  <Button variant="outline" size="sm" disabled={saving} onClick={() => run('admin_set_crew_referrer_status', { p_staff_member_id: staffId, p_active: false }, 'Crew referrals paused')}>
                    <PauseCircle size={16}/> Pause Referrals
                  </Button>
                ) : (
                  <Button size="sm" disabled={saving} onClick={() => run('admin_set_crew_referrer_status', { p_staff_member_id: staffId, p_active: true }, 'Crew referrals resumed')}>
                    <PlayCircle size={16}/> Resume Referrals
                  </Button>
                )}
                <button type="button" onClick={load} className="rounded-lg p-2 text-slate-400 hover:bg-white/5 hover:text-white" aria-label="Refresh referral programme"><RefreshCw size={17}/></button>
              </div>

              {referralUrl ? (
                <div className="space-y-2">
                  <label className="type-label text-slate-500">Permanent Referral Link</label>
                  <div className="flex min-w-0 flex-col gap-2 sm:flex-row">
                    <div className="flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-slate-800 bg-slate-950 px-3 py-2.5 text-sm text-slate-300">
                      <Link2 size={15} className="shrink-0 text-brand-neonBlue"/><span className="truncate">{referralUrl}</span>
                    </div>
                    <Button variant="outline" size="sm" onClick={copyLink}><Copy size={15}/> Copy Link</Button>
                    <a href={referralUrl} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center rounded-lg border border-white/10 px-3 py-2 text-sm text-slate-300 hover:border-brand-neonBlue/40 hover:text-white"><ExternalLink size={15}/><span className="sr-only">Open referral link</span></a>
                  </div>
                </div>
              ) : <p className="text-sm text-amber-300">A website base URL is required before the public link can be displayed.</p>}
            </div>
            {referralUrl && (
              <div className="rounded-2xl bg-white p-4 text-center" aria-label="QR code for permanent Crew referral link">
                <QRCodeSVG value={referralUrl} size={140} level="M" className="mx-auto h-auto w-full max-w-[140px]"/>
                <p className="type-label mt-2 text-slate-700">Permanent Crew Link</p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {[
              ['Total referrals', programme.statistics.total_referrals],
              ['Pending rewards', programme.statistics.pending_rewards],
              ['Pending benefits', programme.statistics.pending_client_benefits],
              ['Terms versions', programme.terms_version_count]
            ].map(([label, value]) => <div key={label} className="rounded-xl border border-white/8 bg-white/[0.025] p-3"><p className="type-numeric text-xl text-white">{value}</p><p className="type-label mt-1 text-slate-500">{label}</p></div>)}
          </div>

          <div className="space-y-4 border-t border-white/5 pt-5">
            <div className="flex flex-wrap items-end justify-between gap-2">
              <div>
                <h5 className="type-heading text-sm text-white">Current Commercial Terms</h5>
                <p className="mt-1 text-xs text-slate-500">Saving creates a new version; historical referral snapshots remain unchanged.</p>
              </div>
              {programme.current_terms && <p className="text-xs text-slate-400">Version {programme.current_terms.version_number} · {new Date(programme.current_terms.effective_from).toLocaleDateString('en-NZ')}</p>}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <TermControl label="Referral reward" type={draft.partner_reward_type} value={draft.partner_reward_value}
                onType={value => setDraft(current => ({ ...current, partner_reward_type: value }))}
                onValue={value => setDraft(current => ({ ...current, partner_reward_value: value }))}/>
              <TermControl label="Client benefit" type={draft.client_benefit_type} value={draft.client_benefit_value}
                onType={value => setDraft(current => ({ ...current, client_benefit_type: value }))}
                onValue={value => setDraft(current => ({ ...current, client_benefit_value: value }))}/>
            </div>
            <Button size="sm" disabled={saving} onClick={saveTerms}><Save size={16}/> Create New Terms Version</Button>
          </div>
        </>
      )}
    </section>
  );
};

const TermControl: React.FC<{
  label: string; type: TermsType; value: string; onType: (value: TermsType) => void; onValue: (value: string) => void;
}> = ({ label, type, value, onType, onValue }) => (
  <fieldset className="rounded-2xl border border-white/8 bg-white/[0.025] p-4 space-y-3">
    <legend className="type-label px-1 text-slate-400">{label}</legend>
    <select aria-label={`${label} type`} value={type} onChange={event => onType(event.target.value as TermsType)} className="w-full rounded-xl border border-slate-800 bg-slate-950 p-3 text-sm text-white outline-none focus:border-brand-neonBlue">
      <option value="NONE">None</option>
      <option value="FIXED_AMOUNT">Fixed amount</option>
      <option value="PERCENTAGE">Percentage</option>
    </select>
    {type !== 'NONE' && (
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">{type === 'FIXED_AMOUNT' ? '$' : '%'}</span>
        <input aria-label={`${label} value`} type="number" min="0" max={type === 'PERCENTAGE' ? 100 : undefined} step="0.01" value={value} onChange={event => onValue(event.target.value)} className="w-full rounded-xl border border-slate-800 bg-slate-950 py-3 pl-8 pr-3 text-sm text-white outline-none focus:border-brand-neonBlue"/>
      </div>
    )}
  </fieldset>
);
